import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { MagnifyingGlassIcon, MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { BulkBar } from "../../../../components/utils/BulkBar";
import {
  complianceErrorMessage,
  getEmployeeRoutines,
  updateEmployeeDocumentAccess,
} from "../../../../api/hse/documentCompliance";
import { promangToast } from "../../../../operationFeedback";
import {
  ComplianceDueDate,
  ComplianceKpi,
  ComplianceSourceChips,
  ComplianceStatusBadge,
  PostponementDots,
} from "../../../../components/dashboard/hse/compliance/ComplianceBits";
import {
  groupEmployeeRoutines,
  ROUTINE_FILTERS,
} from "../../../../components/dashboard/hse/compliance/complianceFilters";

/**
 * One employee's HMS routines: access (and where it comes from), requirement, status and
 * deadline. Admins add/remove the person as reader for one or many routines at once.
 */
export const HseKsEmployeeRoutines = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: !prev.data }));
    getEmployeeRoutines(userId)
      .then((data) => !cancelled && setState({ loading: false, error: false, data }))
      .catch(() => !cancelled && setState({ loading: false, error: true, data: null }));
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => load(), [load]);

  const data = state.data;
  const canManage = Boolean(data?.can_manage);
  const documents = useMemo(() => data?.documents || [], [data]);
  const groups = useMemo(
    () => groupEmployeeRoutines(documents, { query, filter, ungrouped: t("hseCompliance.routines.no_chapter") }),
    [documents, query, filter, t]
  );
  const byId = useMemo(() => new Map(documents.map((doc) => [doc.id, doc])), [documents]);
  const summary = data?.summary || {};

  const change = async (documentIds, grant) => {
    if (!documentIds.length || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const next = await updateEmployeeDocumentAccess(userId, documentIds, grant);
      setState({ loading: false, error: false, data: next });
      setSelected([]);
      promangToast.success(
        grant
          ? t("hseCompliance.routines.granted", { count: documentIds.length })
          : t("hseCompliance.routines.revoked", { count: documentIds.length })
      );
    } catch (error) {
      setActionError(complianceErrorMessage(error, t("hseCompliance.routines.change_error")));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  const visibleIds = groups.flatMap((group) => group.documents.map((doc) => doc.id));
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const selectedDocs = selected.map((id) => byId.get(id)).filter(Boolean);
  const grantable = selectedDocs.filter((doc) => !doc.access.manual && !doc.access.exempt_as_creator).map((doc) => doc.id);
  const removable = selectedDocs.filter((doc) => doc.access.manual).map((doc) => doc.id);

  const kpi = (key, value, tone) => (
    <ComplianceKpi
      label={t(`hseCompliance.routines.kpi_${key}`)}
      value={value || 0}
      tone={tone}
      pressed={filter === key}
      onClick={() => setFilter(filter === key ? "all" : key)}
    />
  );

  return (
    <HsePageChrome
      title={data?.user?.name || t("hseCompliance.routines.title")}
      subtitle={data?.user ? [data.user.position, t("hseCompliance.routines.subtitle")].filter(Boolean).join(" · ") : t("hseCompliance.routines.subtitle")}
      backTo="/dashboard/hse-ks/documents/employees"
    >
      <div className="mx-auto mt-8 max-w-7xl px-4 pb-28 sm:px-6">
        {state.loading ? (
          <ResourceLoading />
        ) : state.error || !data ? (
          <ResourceError backTo="/dashboard/hse-ks/documents/employees" />
        ) : (
          <div className="space-y-5">
            {data.user?.current_employee === false ? (
              <div className="pm-alert pm-alert--warn" role="status">
                <span className="pm-alert__icon" aria-hidden="true">!</span>
                <div>
                  <div className="pm-alert__title">{t("hseCompliance.routines.former_title")}</div>
                  <div className="pm-alert__desc">{t("hseCompliance.routines.former_desc")}</div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {kpi("overdue", summary.overdue_count, summary.overdue_count ? "bad" : undefined)}
              {kpi("pending", summary.pending_count, summary.pending_count ? "warn" : undefined)}
              {kpi("acknowledged", summary.acknowledged_count, "ok")}
              {kpi("no_access", summary.without_access_count)}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <label className="pm-picker-search min-w-0 flex-1 bg-[color:var(--pls-surface)]" style={{ height: 42, borderColor: "var(--pls-border)" }}>
                <MagnifyingGlassIcon className="h-4 w-4 text-[color:var(--pls-muted)]" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("hseCompliance.routines.search")}
                  aria-label={t("hseCompliance.routines.search")}
                />
              </label>
              <div className="pm-segmented pm-segmented--sm lg:w-[560px]" role="radiogroup" aria-label={t("hseCompliance.routines.filter")}>
                {ROUTINE_FILTERS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={filter === value}
                    className={`pm-segmented__option${filter === value ? " is-active" : ""}`}
                    onClick={() => setFilter(value)}
                  >
                    {t(`hseCompliance.routines.filter_${value}`)}
                  </button>
                ))}
              </div>
            </div>

            {canManage ? (
              <p className="text-[13px] text-[color:var(--pls-muted)]">{t("hseCompliance.routines.manage_hint")}</p>
            ) : null}
            {actionError ? (
              <div className="pm-alert pm-alert--bad" role="alert">
                <span className="pm-alert__icon" aria-hidden="true">!</span>
                <div>
                  <div className="pm-alert__title">{t("hseCompliance.routines.change_error")}</div>
                  <div className="pm-alert__desc">{actionError}</div>
                </div>
              </div>
            ) : null}

            {groups.length === 0 ? (
              <div className="pm-surface-card text-center">
                <div className="text-[15px] font-semibold">{t("hseCompliance.routines.empty_title")}</div>
                <p className="mt-1 text-[13px] text-[color:var(--pls-muted)]">{t("hseCompliance.routines.empty_desc")}</p>
                {(query || filter !== "all") && (
                  <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm mt-3" onClick={() => { setQuery(""); setFilter("all"); }}>
                    {t("hseCompliance.employees.clear_filters")}
                  </button>
                )}
              </div>
            ) : (
              <>
              {/* Mobile first: one card per routine below sm, kit table from sm. */}
              <div className="flex flex-col gap-3 sm:hidden">
                {groups.map((group) => (
                  <section key={group.label} className="flex flex-col gap-2">
                    <div className="pm-eyebrow" style={{ color: "var(--pls-faint)" }}>{group.label}</div>
                    {group.documents.map((doc) => {
                      const req = doc.requirement;
                      const isSelected = selected.includes(doc.id);
                      return (
                        <article
                          key={doc.id}
                          className={`rounded-[14px] border bg-[color:var(--pls-surface)] p-3 ${isSelected ? "border-[color:var(--pls-accent)]" : "border-[color:var(--pls-border)]"}`}
                        >
                          <div className="flex items-start gap-3">
                            {canManage ? (
                              <input
                                type="checkbox"
                                className="pm-checkbox mt-1"
                                aria-label={t("hseCompliance.routines.select_one", { title: doc.title })}
                                checked={isSelected}
                                onChange={() => toggle(doc.id)}
                              />
                            ) : null}
                            <div className="min-w-0 flex-1">
                              <Link to={`/dashboard/hse-ks/documents/${doc.id}`} className="font-semibold">{doc.title}</Link>
                              {doc.document_number ? <div className="pm-table__code">{doc.document_number}</div> : null}
                            </div>
                            <ComplianceStatusBadge status={doc.status} via={doc.acknowledged_via} />
                          </div>
                          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                            <ComplianceSourceChips
                              sources={doc.access.sources}
                              trainingGuides={doc.access.training_guides}
                              emptyLabel={t("hseCompliance.routines.no_access")}
                            />
                            {req && ["pending", "overdue"].includes(doc.status) ? (
                              <span className="flex items-center gap-2">
                                <ComplianceDueDate dueAt={req.due_at} overdue={req.overdue} />
                                <PostponementDots used={req.postponement_count} max={req.max_postponements} />
                              </span>
                            ) : null}
                          </div>
                          {canManage && !doc.access.exempt_as_creator ? (
                            <div className="mt-2 flex justify-end">
                              {doc.access.manual ? (
                                <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" disabled={busy} onClick={() => change([doc.id], false)}>
                                  {t("hseCompliance.routines.remove")}
                                </button>
                              ) : (
                                <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm" disabled={busy} onClick={() => change([doc.id], true)}>
                                  {doc.access.has_access ? t("hseCompliance.routines.add_manual") : t("hseCompliance.routines.grant")}
                                </button>
                              )}
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </section>
                ))}
              </div>
              <div className="hidden overflow-x-auto rounded-[14px] border border-[color:var(--pls-border)] bg-[color:var(--pls-surface)] sm:block">
                <table className="pm-table">
                  <thead>
                    <tr>
                      {canManage ? (
                        <th className="w-10">
                          <input
                            type="checkbox"
                            className="pm-checkbox"
                            aria-label={t("hseCompliance.routines.select_all")}
                            checked={allVisibleSelected}
                            onChange={() =>
                              setSelected(allVisibleSelected ? selected.filter((id) => !visibleIds.includes(id)) : [...new Set([...selected, ...visibleIds])])
                            }
                          />
                        </th>
                      ) : null}
                      <th>{t("hseCompliance.routines.col_document")}</th>
                      <th>{t("hseCompliance.routines.col_access")}</th>
                      <th>{t("hseCompliance.routines.col_status")}</th>
                      <th>{t("hseCompliance.routines.col_due")}</th>
                      {canManage ? <th className="text-right">{t("hseCompliance.routines.col_action")}</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => (
                      <React.Fragment key={group.label}>
                        <tr className="pm-table__group">
                          <td colSpan={canManage ? 6 : 4}>{group.label}</td>
                        </tr>
                        {group.documents.map((doc) => {
                          const isSelected = selected.includes(doc.id);
                          const req = doc.requirement;
                          return (
                            <tr key={doc.id} className={isSelected ? "is-selected" : ""}>
                              {canManage ? (
                                <td>
                                  <input
                                    type="checkbox"
                                    className="pm-checkbox"
                                    aria-label={t("hseCompliance.routines.select_one", { title: doc.title })}
                                    checked={isSelected}
                                    onChange={() => toggle(doc.id)}
                                  />
                                </td>
                              ) : null}
                              <td className="min-w-[240px]">
                                <Link to={`/dashboard/hse-ks/documents/${doc.id}`} className="font-semibold hover:text-[color:var(--pls-accent)]">
                                  {doc.title}
                                </Link>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                                  {doc.document_number ? <span className="pm-table__code">{doc.document_number}</span> : null}
                                  {doc.read_requirement === "required" ? (
                                    <span className="text-[11.5px] text-[color:var(--pls-muted)]">{t("hseCompliance.routines.required")}</span>
                                  ) : (
                                    <span className="text-[11.5px] text-[color:var(--pls-faint)]">{t("hseCompliance.routines.not_required")}</span>
                                  )}
                                </div>
                              </td>
                              <td className="min-w-[180px]">
                                <ComplianceSourceChips
                                  sources={doc.access.sources}
                                  trainingGuides={doc.access.training_guides}
                                  emptyLabel={t("hseCompliance.routines.no_access")}
                                />
                              </td>
                              <td><ComplianceStatusBadge status={doc.status} via={doc.acknowledged_via} /></td>
                              <td>
                                {req && ["pending", "overdue"].includes(doc.status) ? (
                                  <span className="flex flex-col gap-1">
                                    <ComplianceDueDate dueAt={req.due_at} overdue={req.overdue} />
                                    <PostponementDots used={req.postponement_count} max={req.max_postponements} />
                                  </span>
                                ) : (
                                  <span className="text-[color:var(--pls-faint)]">—</span>
                                )}
                              </td>
                              {canManage ? (
                                <td className="text-right">
                                  {doc.access.exempt_as_creator ? null : doc.access.manual ? (
                                    <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" disabled={busy} onClick={() => change([doc.id], false)}>
                                      {t("hseCompliance.routines.remove")}
                                    </button>
                                  ) : (
                                    <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm" disabled={busy} onClick={() => change([doc.id], true)}>
                                      {doc.access.has_access ? t("hseCompliance.routines.add_manual") : t("hseCompliance.routines.grant")}
                                    </button>
                                  )}
                                </td>
                              ) : null}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        )}
      </div>
      {canManage ? (
        <BulkBar
          count={selected.length}
          onClear={() => setSelected([])}
          actions={[
            {
              label: t("hseCompliance.routines.bulk_grant", { count: grantable.length }),
              icon: PlusCircleIcon,
              action: () => change(grantable, true),
              disabled: busy || grantable.length === 0,
            },
            { separator: true },
            {
              label: t("hseCompliance.routines.bulk_remove", { count: removable.length }),
              icon: MinusCircleIcon,
              action: () => change(removable, false),
              disabled: busy || removable.length === 0,
              danger: true,
            },
          ]}
        />
      ) : null}
    </HsePageChrome>
  );
};

export default HseKsEmployeeRoutines;
